/**
 * Inspection Evidence Uploader Component
 * 
 * Multi-file uploader per inspection item with preview thumbnails
 */

import { useState, useRef } from 'react';
import { Camera, Upload, X, Loader2, Image as ImageIcon, Video, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { uploadInspectionEvidence, getEvidenceSignedUrl, deleteInspectionEvidence } from '@/lib/inspections/evidenceUpload';
import type { InspectionEvidence } from '@/hooks/useInspectionExecution';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface InspectionEvidenceUploaderProps {
  shopId: string;
  ticketId: string;
  inspectionItemId: string;
  itemKey: string;
  sharedKey?: string | null;
  evidence: InspectionEvidence[];
  disabled?: boolean;
  onUploadComplete?: () => void;
}

function EvidenceTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'photo':
      return <ImageIcon className="h-4 w-4" />;
    case 'video':
      return <Video className="h-4 w-4" />;
    default:
      return <File className="h-4 w-4" />;
  }
}

function EvidenceThumbnail({ 
  evidence, 
  onDelete,
  disabled,
}: { 
  evidence: InspectionEvidence;
  onDelete: () => void;
  disabled: boolean;
}) {
  const { data: signedUrl } = useQuery({
    queryKey: ['evidence-url', evidence.id],
    queryFn: () => getEvidenceSignedUrl(evidence.storage_path),
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  const isImage = evidence.evidence_type === 'photo';

  return (
    <div className="relative group w-16 h-16 rounded-md overflow-hidden border bg-muted">
      {isImage && signedUrl ? (
        <img 
          src={signedUrl} 
          alt="Evidence" 
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <EvidenceTypeIcon type={evidence.evidence_type} />
        </div>
      )}
      
      {!disabled && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute -top-1 -right-1 p-0.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function InspectionEvidenceUploader({
  shopId,
  ticketId,
  inspectionItemId,
  itemKey,
  sharedKey,
  evidence,
  disabled = false,
  onUploadComplete,
}: InspectionEvidenceUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    
    try {
      for (const file of Array.from(files)) {
        await uploadInspectionEvidence({
          shopId,
          ticketId,
          inspectionItemId,
          file,
          source: 'technician',
          sharedKey: sharedKey ?? undefined,
          itemKey,
        });
      }

      toast({
        title: 'Evidence uploaded',
        description: `${files.length} file(s) uploaded successfully.`,
      });

      // Invalidate queries to refresh evidence
      queryClient.invalidateQueries({ queryKey: ['ticket-inspection-evidence'] });
      onUploadComplete?.();
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload evidence.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (evidenceId: string) => {
    setIsDeleting(evidenceId);
    try {
      await deleteInspectionEvidence(evidenceId);
      queryClient.invalidateQueries({ queryKey: ['ticket-inspection-evidence'] });
      toast({
        title: 'Evidence deleted',
      });
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete evidence.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="space-y-2">
      {/* Evidence thumbnails */}
      {evidence.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {evidence.map((ev) => (
            <div key={ev.id} className={cn(isDeleting === ev.id && 'opacity-50')}>
              <EvidenceThumbnail
                evidence={ev}
                onDelete={() => handleDelete(ev.id)}
                disabled={disabled || isDeleting !== null}
              />
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {!disabled && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,.pdf"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="gap-2"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Camera className="h-4 w-4" />
                Add Photo/Video
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
